import { SupportedLocaleEnum } from 'localization';
import {
  ProductItem,
  SellingPrice,
} from 'lane-shared/domains/billingPayments/types';
import {
  type SupportedPaymentCurrency,
  getCurrencyPrecision,
} from 'domains-billing-currency';
import { getPricing } from '../../productCatalog/helpers';
import { PriceComponents } from '../../productCatalog/types';
import BigNumber from 'bignumber.js';

function hasSellingPrice(sellingPrice?: SellingPrice): boolean {
  return sellingPrice?.listPrice !== undefined;
}

export function getDraftTotalAmounts(
  listOfProducts: ProductItem[],
  currency: SupportedPaymentCurrency,
  locale: SupportedLocaleEnum,
  isAdminFeeFeatureEnabled = false
) {
  const precision = getCurrencyPrecision(currency);

  let subTotal = new BigNumber(0);
  let tax = new BigNumber(0);
  let adminFee = new BigNumber(0);
  let adminFeeTax = new BigNumber(0);

  listOfProducts.forEach(productItem => {
    const priceComponents: PriceComponents = {
      amount: productItem.product.amount,
      markup: productItem.product.markup,
      adminFee: productItem.product.adminFee,
      adminFeeTax: productItem.product.adminFeeTax,
      tax: productItem.product.tax,
      netPrice: productItem.product.netPrice,
      sellingPrice: productItem.product.sellingPrice,
    };

    const pricing = getPricing({
      currency,
      locale,
      priceComponents,
      isAdminFeeFeatureEnabled,
    });

    const quantity = new BigNumber(productItem.quantity);

    const adminFeeForProduct = new BigNumber(pricing.grossAdminFee);
    const adminFeeTaxForProduct = new BigNumber(pricing.grossAdminFeeTax);

    if (
      isAdminFeeFeatureEnabled &&
      hasSellingPrice(productItem.product.sellingPrice)
    ) {
      subTotal = subTotal.plus(
        new BigNumber(pricing.listPrice)
          .multipliedBy(quantity)
          .decimalPlaces(precision)
      );
      adminFee = adminFee.plus(
        adminFeeForProduct.multipliedBy(quantity).decimalPlaces(precision)
      );
      adminFeeTax = adminFeeTax.plus(
        adminFeeTaxForProduct.multipliedBy(quantity).decimalPlaces(precision)
      );
    } else if (
      isAdminFeeFeatureEnabled &&
      (adminFeeForProduct.gt(0) || adminFeeTaxForProduct.gt(0))
    ) {
      const listPriceWithoutAdminFee = new BigNumber(pricing.listPrice)
        .minus(adminFeeForProduct)
        .minus(adminFeeTaxForProduct);

      subTotal = subTotal.plus(
        listPriceWithoutAdminFee
          .multipliedBy(quantity)
          .decimalPlaces(precision)
      );
      adminFee = adminFee.plus(
        adminFeeForProduct.multipliedBy(quantity).decimalPlaces(precision)
      );
      adminFeeTax = adminFeeTax.plus(
        adminFeeTaxForProduct.multipliedBy(quantity).decimalPlaces(precision)
      );
    } else {
      subTotal = subTotal.plus(
        new BigNumber(pricing.listPrice)
          .multipliedBy(quantity)
          .decimalPlaces(precision)
      );
    }

    tax = tax.plus(
      new BigNumber(pricing.grossTax)
        .multipliedBy(quantity)
        .decimalPlaces(precision)
    );
  });

  const total = subTotal.plus(tax).plus(adminFee).plus(adminFeeTax);

  return {
    subTotal: subTotal.toNumber(),
    tax: tax.toNumber(),
    adminFee: adminFee.toNumber(),
    adminFeeTax: adminFeeTax.toNumber(),
    total: total.toNumber(),
  };
}
